import { useEffect, useRef, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  FileText,
  Upload,
  Download,
  Trash2,
  Loader2,
  Sparkles,
  FolderOpen,
  UploadCloud,
  X,
  CheckCircle2,
  AlertCircle,
  Clock,
  UserCircle2,
  Eye,
  Printer,
  FormInput,
  LayoutTemplate,
  BookmarkPlus,
} from "lucide-react";
import FilePreviewModal from "./FilePreviewModal";
import FillableFormEditor from "./FillableFormEditor";
import IdentityApproveDialog from "./IdentityApproveDialog";
import FormRequestsPanel from "./FormRequestsPanel";
import { usePermissions } from "@/context/PermissionsContext";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const CATEGORIES = [
  { value: "i9", label: "I-9" },
  { value: "w4", label: "W-4" },
  { value: "direct_deposit", label: "Direct Deposit" },
  { value: "background_check", label: "Background Check" },
  { value: "drug_screen", label: "Drug Screen" },
  { value: "tb_test", label: "TB Test" },
  { value: "license", label: "License / Nursing License" },
  { value: "certification", label: "Certification" },
  { value: "orientation_packet", label: "Orientation Packet" },
  { value: "inservice", label: "In-service Training" },
  { value: "handbook_ack", label: "Handbook Ack" },
  { value: "driver_license", label: "Driver's License" },
  { value: "ssn_card", label: "Social Security Card" },
  { value: "auto_insurance", label: "Auto Insurance" },
  { value: "cpr_bls", label: "CPR / BLS" },
  { value: "immunization", label: "Immunization" },
  { value: "other", label: "Other" },
];

const CATEGORY_LABEL = Object.fromEntries(CATEGORIES.map((c) => [c.value, c.label]));

const formatBytes = (b) => {
  if (!b) return "";
  if (b < 1024) return `${b} B`;
  if (b < 1024 * 1024) return `${(b / 1024).toFixed(1)} KB`;
  return `${(b / (1024 * 1024)).toFixed(1)} MB`;
};

export default function EmployeeFilesTab({ employeeId, isAdmin }) {
  const { hasPermission } = usePermissions();
  const canDownload = hasPermission("documents", "download");
  const [files, setFiles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [category, setCategory] = useState("other");
  const [description, setDescription] = useState("");
  const [filter, setFilter] = useState("all");

  // Bulk upload state
  const [queue, setQueue] = useState([]); // {tmpId, file, category, description, status, error}
  const [dragOver, setDragOver] = useState(false);
  const [bulkUploading, setBulkUploading] = useState(false);
  const bulkInputRef = useRef(null);

  // Preview modal + personnel summary
  const [previewFile, setPreviewFile] = useState(null);
  const [identityApproveFile, setIdentityApproveFile] = useState(null);
  const [buildingSummary, setBuildingSummary] = useState(false);
  const [fillableFile, setFillableFile] = useState(null);
  const [templates, setTemplates] = useState([]);
  const [assigningTemplate, setAssigningTemplate] = useState(false);

  useEffect(() => {
    if (!isAdmin) return;
    axios.get(`${API}/form-templates`).then((r) => setTemplates(r.data.templates || [])).catch(() => {});
  }, [isAdmin]);

  const assignTemplate = async (templateId) => {
    if (!templateId) return;
    setAssigningTemplate(true);
    try {
      const res = await axios.post(`${API}/form-templates/${templateId}/assign`, { employee_ids: [employeeId] });
      if (res.data.assigned_count > 0) toast.success("Form assigned — it now appears in the employee's Forms to Fill");
      else toast.info("This employee already has that form waiting to be filled");
      fetchFiles();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Assign failed");
    } finally {
      setAssigningTemplate(false);
    }
  };

  const saveAsTemplate = async (f) => {
    const title = window.prompt("Template title", (f.filename || "").replace(/\.[^.]+$/, ""));
    if (title === null) return;
    try {
      await axios.post(`${API}/form-templates/from-file/${employeeId}/${f.id}`, { title, category: f.category });
      toast.success("Saved to Form Templates — assign it to anyone from Settings → Form Templates");
      const r = await axios.get(`${API}/form-templates`);
      setTemplates(r.data.templates || []);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not save template");
    }
  };

  useEffect(() => {
    fetchFiles();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [employeeId]);

  const fetchFiles = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/employees/${employeeId}/files`);
      setFiles(res.data.files || []);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const handleUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const form = new FormData();
      form.append("file", file);
      form.append("category", category);
      form.append("description", description);
      await axios.post(`${API}/employees/${employeeId}/files`, form, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      toast.success("File uploaded");
      setDescription("");
      fetchFiles();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Upload failed");
    } finally {
      setUploading(false);
      e.target.value = "";
    }
  };

  // ---------- Bulk upload helpers ----------
  const addFilesToQueue = (fileList) => {
    const incoming = Array.from(fileList || []);
    if (!incoming.length) return;
    const rows = incoming.map((f) => ({
      tmpId: `${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
      file: f,
      category: "",
      description: "",
      status: "pending", // pending | uploading | success | error
      error: "",
    }));
    setQueue((prev) => [...prev, ...rows]);
  };

  const onBulkPick = (e) => {
    addFilesToQueue(e.target.files);
    e.target.value = "";
  };

  const onDrop = (e) => {
    e.preventDefault();
    setDragOver(false);
    addFilesToQueue(e.dataTransfer.files);
  };

  const updateRow = (tmpId, patch) => {
    setQueue((prev) => prev.map((r) => (r.tmpId === tmpId ? { ...r, ...patch } : r)));
  };

  const removeRow = (tmpId) => {
    setQueue((prev) => prev.filter((r) => r.tmpId !== tmpId));
  };

  const clearQueue = () => setQueue([]);

  const uploadRow = async (row) => {
    updateRow(row.tmpId, { status: "uploading", error: "" });
    try {
      const form = new FormData();
      form.append("file", row.file);
      form.append("category", row.category);
      form.append("description", row.description || "");
      await axios.post(`${API}/employees/${employeeId}/files`, form, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      updateRow(row.tmpId, { status: "success" });
      return { ok: true };
    } catch (err) {
      const msg = err.response?.data?.detail || err.message || "Upload failed";
      updateRow(row.tmpId, { status: "error", error: msg });
      return { ok: false, error: msg };
    }
  };

  const uploadAll = async () => {
    // Validate categories present on every row that isn't already done
    const pending = queue.filter((r) => r.status !== "success");
    const missing = pending.filter((r) => !r.category);
    if (missing.length) {
      toast.error(`Pick a category for ${missing.length} file${missing.length > 1 ? "s" : ""} before uploading`);
      return;
    }
    if (!pending.length) {
      toast.info("Nothing to upload");
      return;
    }

    setBulkUploading(true);
    // Cap concurrency at 4 so we don't overwhelm the network / storage
    const concurrency = 4;
    let cursor = 0;
    const workers = Array.from({ length: Math.min(concurrency, pending.length) }, async () => {
      while (cursor < pending.length) {
        const idx = cursor++;
        // Read fresh row from state closure via ref-like snapshot
        // eslint-disable-next-line no-await-in-loop
        await uploadRow(pending[idx]);
      }
    });
    await Promise.all(workers);
    setBulkUploading(false);

    // Refresh main list and summarise
    await fetchFiles();
    const successCount = pending.filter((r) => {
      // Re-read status from queue state (freshest) via functional get
      return true;
    }).length;
    toast.success(`Bulk upload complete (${successCount} file${successCount === 1 ? "" : "s"} processed)`);
  };

  const removeCompleted = () => {
    setQueue((prev) => prev.filter((r) => r.status !== "success"));
  };

  // ---------- End bulk helpers ----------

  const downloadFile = async (f) => {
    try {
      const res = await fetch(`${API}/employees/${employeeId}/files/${f.id}`, {
        headers: { Authorization: `Bearer ${localStorage.getItem("token") || ""}` },
      });
      if (!res.ok) throw new Error();
      const blob = await res.blob();
      const link = document.createElement("a");
      link.href = URL.createObjectURL(blob);
      link.download = f.filename;
      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch {
      toast.error("Download failed");
    }
  };

  const deleteFile = async (f) => {
    if (!window.confirm(`Delete "${f.filename}"? This cannot be undone.`)) return;
    try {
      await axios.delete(`${API}/employees/${employeeId}/files/${f.id}`);
      toast.success("File deleted");
      fetchFiles();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Delete failed");
    }
  };

  const reviewFile = async (f, action) => {
    if (action === "approve" && f.identity_doc) {
      setIdentityApproveFile(f);
      return;
    }
    let reason = "";
    if (action === "reject") {
      reason = window.prompt(`Reject "${f.filename}" — reason (visible to employee):`);
      if (reason === null) return; // cancelled
      if (!reason.trim()) {
        toast.error("Rejection reason is required");
        return;
      }
    }
    try {
      await axios.post(`${API}/employees/${employeeId}/files/${f.id}/review`, {
        action,
        rejection_reason: reason || undefined,
      });
      toast.success(action === "approve" ? "Document approved" : "Document rejected");
      fetchFiles();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Review failed");
    }
  };

  const printPersonnelSummary = async () => {
    setBuildingSummary(true);
    try {
      const res = await axios.get(
        `${API}/employees/${employeeId}/personnel-summary.pdf?inline=true`,
        { responseType: "blob" }
      );
      const url = URL.createObjectURL(res.data);
      // Open in a new tab; the tab will auto-focus a print dialog.
      const win = window.open(url, "_blank");
      if (!win) {
        toast.error("Please allow pop-ups to open the summary");
        URL.revokeObjectURL(url);
        return;
      }
      // Give the tab a moment to render then trigger print
      const trigger = () => {
        try { win.focus(); win.print(); } catch { /* user can Ctrl+P */ }
      };
      // Some browsers block cross-blob print calls; user can Ctrl+P as fallback.
      setTimeout(trigger, 900);
      toast.success("Personnel summary ready");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not build summary");
    } finally {
      setBuildingSummary(false);
    }
  };

  const filtered = filter === "all" ? files : files.filter((f) => f.category === filter);

  const successRows = queue.filter((r) => r.status === "success").length;
  const errorRows = queue.filter((r) => r.status === "error").length;

  return (
    <div className="space-y-4" data-testid="employee-files-tab">
      <FormRequestsPanel employeeId={employeeId} onChanged={fetchFiles} />

      {/* Bulk drop zone */}
      <div className="bg-white border border-slate-200 rounded-xl p-4" data-testid="bulk-upload-card">
        <div className="flex items-center justify-between gap-2 mb-3">
          <div className="flex items-center gap-2">
            <UploadCloud className="w-5 h-5 text-emerald-600" />
            <h3 className="font-semibold text-slate-900">Bulk Upload</h3>
            <Badge variant="outline" className="bg-emerald-50 text-emerald-700 border-emerald-200 text-xs">
              Drop 5+ files at once
            </Badge>
          </div>
          {queue.length > 0 && (
            <div className="flex items-center gap-2 text-xs text-slate-600">
              <span>{queue.length} queued</span>
              {successRows > 0 && <span className="text-emerald-600">· {successRows} done</span>}
              {errorRows > 0 && <span className="text-red-600">· {errorRows} failed</span>}
            </div>
          )}
        </div>

        <div
          onDragOver={(e) => {
            e.preventDefault();
            setDragOver(true);
          }}
          onDragLeave={() => setDragOver(false)}
          onDrop={onDrop}
          onClick={() => bulkInputRef.current?.click()}
          className={`border-2 border-dashed rounded-xl p-6 text-center cursor-pointer transition-colors ${
            dragOver
              ? "border-emerald-500 bg-emerald-50"
              : "border-slate-300 bg-slate-50 hover:bg-slate-100"
          }`}
          data-testid="bulk-upload-dropzone"
        >
          <UploadCloud className={`w-8 h-8 mx-auto mb-2 ${dragOver ? "text-emerald-600" : "text-slate-400"}`} />
          <p className="text-sm text-slate-700 font-medium">
            {dragOver ? "Release to add files" : "Drag & drop files here, or click to browse"}
          </p>
          <p className="text-xs text-slate-500 mt-1">
            PDFs, images, docs · up to 100 MB per file · assign a category to each below
          </p>
          <input
            ref={bulkInputRef}
            type="file"
            multiple
            className="hidden"
            onChange={onBulkPick}
            data-testid="bulk-upload-input"
          />
        </div>

        {queue.length > 0 && (
          <div className="mt-4 border border-slate-200 rounded-lg overflow-hidden">
            <div className="max-h-96 overflow-y-auto">
              <table className="w-full text-sm">
                <thead className="bg-slate-50 text-slate-600 text-xs uppercase tracking-wide">
                  <tr>
                    <th className="text-left px-3 py-2 font-medium">File</th>
                    <th className="text-left px-3 py-2 font-medium w-48">Category *</th>
                    <th className="text-left px-3 py-2 font-medium">Description</th>
                    <th className="text-center px-3 py-2 font-medium w-20">Status</th>
                    <th className="w-10"></th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {queue.map((row) => (
                    <tr key={row.tmpId} data-testid={`bulk-row-${row.tmpId}`}>
                      <td className="px-3 py-2">
                        <div className="flex items-center gap-2 min-w-0">
                          <FileText className="w-4 h-4 text-slate-400 flex-shrink-0" />
                          <div className="min-w-0">
                            <p className="truncate font-medium text-slate-800" title={row.file.name}>
                              {row.file.name}
                            </p>
                            <p className="text-xs text-slate-500">{formatBytes(row.file.size)}</p>
                          </div>
                        </div>
                      </td>
                      <td className="px-3 py-2">
                        <Select
                          value={row.category}
                          onValueChange={(v) => updateRow(row.tmpId, { category: v })}
                          disabled={row.status === "uploading" || row.status === "success"}
                        >
                          <SelectTrigger
                            className={`h-9 ${!row.category ? "border-amber-300" : ""}`}
                            data-testid={`bulk-row-category-${row.tmpId}`}
                          >
                            <SelectValue placeholder="Pick a category" />
                          </SelectTrigger>
                          <SelectContent>
                            {CATEGORIES.map((c) => (
                              <SelectItem key={c.value} value={c.value}>
                                {c.label}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </td>
                      <td className="px-3 py-2">
                        <Input
                          value={row.description}
                          onChange={(e) => updateRow(row.tmpId, { description: e.target.value })}
                          placeholder="Optional notes"
                          disabled={row.status === "uploading" || row.status === "success"}
                          className="h-9"
                          data-testid={`bulk-row-desc-${row.tmpId}`}
                        />
                      </td>
                      <td className="px-3 py-2 text-center">
                        {row.status === "pending" && (
                          <span className="text-xs text-slate-500">Pending</span>
                        )}
                        {row.status === "uploading" && (
                          <Loader2 className="w-4 h-4 animate-spin text-blue-600 mx-auto" />
                        )}
                        {row.status === "success" && (
                          <CheckCircle2 className="w-4 h-4 text-emerald-600 mx-auto" />
                        )}
                        {row.status === "error" && (
                          <div className="flex flex-col items-center" title={row.error}>
                            <AlertCircle className="w-4 h-4 text-red-600" />
                            <span className="text-[10px] text-red-600 truncate max-w-[80px]">
                              {row.error}
                            </span>
                          </div>
                        )}
                      </td>
                      <td className="px-3 py-2 text-right">
                        <button
                          onClick={() => removeRow(row.tmpId)}
                          disabled={row.status === "uploading"}
                          className="text-slate-400 hover:text-red-600 disabled:opacity-30"
                          data-testid={`bulk-row-remove-${row.tmpId}`}
                          aria-label="Remove"
                        >
                          <X className="w-4 h-4" />
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            <div className="flex flex-wrap items-center justify-between gap-2 p-3 bg-slate-50 border-t border-slate-200">
              <div className="flex gap-2">
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={clearQueue}
                  disabled={bulkUploading}
                  data-testid="bulk-clear-btn"
                >
                  Clear all
                </Button>
                {successRows > 0 && (
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={removeCompleted}
                    disabled={bulkUploading}
                    data-testid="bulk-remove-completed-btn"
                  >
                    Remove completed
                  </Button>
                )}
              </div>
              <Button
                onClick={uploadAll}
                disabled={bulkUploading || queue.every((r) => r.status === "success")}
                className="gap-2 bg-emerald-600 hover:bg-emerald-700"
                data-testid="bulk-upload-all-btn"
              >
                {bulkUploading ? (
                  <>
                    <Loader2 className="w-4 h-4 animate-spin" />
                    Uploading...
                  </>
                ) : (
                  <>
                    <UploadCloud className="w-4 h-4" />
                    Upload All ({queue.filter((r) => r.status !== "success").length})
                  </>
                )}
              </Button>
            </div>
          </div>
        )}
      </div>

      {/* Single upload row */}
      <div className="bg-white border border-slate-200 rounded-xl p-4">
        <div className="flex items-center gap-2 mb-3">
          <FolderOpen className="w-5 h-5 text-blue-600" />
          <h3 className="font-semibold text-slate-900">Upload New File</h3>
        </div>
        <div className="grid gap-3 md:grid-cols-4">
          <Select value={category} onValueChange={setCategory}>
            <SelectTrigger data-testid="file-category-select">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {CATEGORIES.map((c) => (
                <SelectItem key={c.value} value={c.value}>{c.label}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Input
            placeholder="Description (optional)"
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            className="md:col-span-2"
            data-testid="file-description-input"
          />
          <label className="inline-flex">
            <input
              type="file"
              className="hidden"
              onChange={handleUpload}
              disabled={uploading}
              data-testid="upload-employee-file-input"
            />
            <Button asChild className="gap-2 w-full cursor-pointer" disabled={uploading}>
              <span>
                {uploading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Upload className="w-4 h-4" />}
                {uploading ? "Uploading..." : "Upload"}
              </span>
            </Button>
          </label>
        </div>
      </div>

      {/* Filter + files list */}
      <div className="bg-white border border-slate-200 rounded-xl p-4">
        <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
          <h3 className="font-semibold text-slate-900">Employee Files ({filtered.length})</h3>
          <div className="flex items-center gap-2">
            {isAdmin && templates.length > 0 && (
              <Select value="" onValueChange={assignTemplate} disabled={assigningTemplate}>
                <SelectTrigger className="w-52 h-9 border-purple-300 text-purple-700" data-testid="assign-template-select">
                  <span className="flex items-center gap-1.5 text-sm">
                    {assigningTemplate ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <LayoutTemplate className="w-3.5 h-3.5" />}
                    Assign form to fill…
                  </span>
                </SelectTrigger>
                <SelectContent>
                  {templates.filter((t) => t.fillable_form?.enabled).map((t) => (
                    <SelectItem key={t.id} value={t.id} data-testid={`assign-template-option-${t.id}`}>
                      {t.title}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            )}
            <Button
              size="sm"
              variant="outline"
              onClick={printPersonnelSummary}
              disabled={buildingSummary || files.length === 0 || !canDownload}
              className="gap-1"
              data-testid="print-personnel-summary-btn"
            >
              {buildingSummary ? (
                <Loader2 className="w-3.5 h-3.5 animate-spin" />
              ) : (
                <Printer className="w-3.5 h-3.5" />
              )}
              Print Personnel Summary
            </Button>
            <Select value={filter} onValueChange={setFilter}>
              <SelectTrigger className="w-48" data-testid="file-filter-select">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All categories</SelectItem>
                {CATEGORIES.map((c) => (
                  <SelectItem key={c.value} value={c.value}>{c.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>

        {loading ? (
          <div className="flex justify-center py-8">
            <Loader2 className="w-5 h-5 animate-spin text-slate-400" />
          </div>
        ) : filtered.length === 0 ? (
          <div className="text-center py-10 text-slate-500 bg-slate-50 rounded-lg">
            <FileText className="w-8 h-8 mx-auto mb-2 opacity-50" />
            <p className="text-sm">No files in this category yet</p>
          </div>
        ) : (
          <ul className="divide-y divide-slate-100">
            {filtered.map((f) => {
              const isPending = f.review_status === "pending_review";
              const isRejected = f.review_status === "rejected";
              return (
              <li key={f.id} className={`py-3 flex items-center gap-3 ${isPending ? "bg-amber-50/40 -mx-4 px-4" : ""}`}>
                <div className="w-10 h-10 rounded-lg bg-slate-100 flex items-center justify-center flex-shrink-0">
                  <FileText className="w-5 h-5 text-slate-500" />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <p className="font-medium text-slate-900 truncate">{f.filename}</p>
                    <Badge variant="outline" className="text-xs bg-slate-50">
                      {CATEGORY_LABEL[f.category] || f.category}
                    </Badge>
                    {f.system_generated && (
                      <Badge variant="outline" className="text-xs bg-purple-50 text-purple-700 border-purple-200 gap-1">
                        <Sparkles className="w-3 h-3" />
                        Auto
                      </Badge>
                    )}
                    {f.uploaded_by_self && (
                      <Badge variant="outline" className="text-xs bg-blue-50 text-blue-700 border-blue-200 gap-1">
                        <UserCircle2 className="w-3 h-3" />
                        Self-Upload
                      </Badge>
                    )}
                    {isPending && (
                      <Badge className="text-xs bg-amber-100 text-amber-800 hover:bg-amber-100 gap-1">
                        <Clock className="w-3 h-3" />
                        Pending Review
                      </Badge>
                    )}
                    {isRejected && (
                      <Badge className="text-xs bg-red-100 text-red-800 hover:bg-red-100 gap-1">
                        <AlertCircle className="w-3 h-3" />
                        Rejected
                      </Badge>
                    )}
                    {f.fillable_form?.enabled && (
                      <Badge className="text-xs bg-purple-100 text-purple-800 hover:bg-purple-100 gap-1">
                        <FormInput className="w-3 h-3" />
                        Fillable · {f.fillable_form.fields?.length || 0} fields
                      </Badge>
                    )}
                  </div>
                  <p className="text-xs text-slate-500 mt-0.5">
                    {formatBytes(f.size)} · Uploaded {new Date(f.uploaded_at).toLocaleString()}
                    {f.uploaded_by_name ? ` by ${f.uploaded_by_name}` : ""}
                  </p>
                  {f.description && (
                    <p className="text-xs text-slate-600 mt-1 italic truncate">{f.description}</p>
                  )}
                  {isRejected && f.rejection_reason && (
                    <p className="text-xs text-red-700 mt-1 bg-red-50 border border-red-100 rounded px-2 py-1 inline-block">
                      HR note: {f.rejection_reason}
                    </p>
                  )}
                  {f.cosign_status && (
                    <p className={`text-xs mt-1 rounded px-2 py-1 inline-block border ${f.cosign_status === "signed" ? "text-emerald-800 bg-emerald-50 border-emerald-100" : "text-rose-800 bg-rose-50 border-rose-100"}`} data-testid={`cosign-status-${f.id}`}>
                      {f.cosign_status === "signed" ? `RN signed by ${f.cosigned_by_name}` : "Awaiting RN signature"}
                    </p>
                  )}
                  {f.certificate && (
                    <p className="text-xs text-amber-800 mt-1 bg-amber-50 border border-amber-100 rounded px-2 py-1 inline-block" data-testid={`certificate-info-${f.id}`}>
                      Certificate {f.certificate.number} · {f.certificate.inservice_title}{f.certificate.hours ? ` · ${f.certificate.hours} h` : ""} · approved by {f.certificate.approved_by_name}
                    </p>
                  )}
                  {f.source === "inservice" && f.certificate_file_id && (
                    <button type="button" onClick={() => downloadFile({ id: f.certificate_file_id, filename: `Certificate - ${f.inservice?.title || "in-service"}.pdf` })} className="text-xs text-teal-700 hover:underline mt-1 inline-flex items-center gap-1 ml-2" data-testid={`download-certificate-${f.id}`}>
                      <Download className="w-3 h-3" /> Download certificate
                    </button>
                  )}
                  {f.identity_details && (f.identity_details.document_title || f.identity_details.document_number) && (
                    <p className="text-xs text-emerald-800 mt-1 bg-emerald-50 border border-emerald-100 rounded px-2 py-1 inline-block" data-testid={`identity-details-${f.id}`}>
                      {f.identity_details.i9_list ? `I-9 List ${f.identity_details.i9_list.toUpperCase()} · ` : ""}
                      {[f.identity_details.document_title, f.identity_details.issuing_authority, f.identity_details.document_number ? `#${f.identity_details.document_number}` : "", f.identity_details.expiration_date ? `exp ${f.identity_details.expiration_date}` : ""].filter(Boolean).join(" · ")}
                      {f.identity_details.verified_by_name ? ` · verified by ${f.identity_details.verified_by_name}` : ""}
                    </p>
                  )}
                </div>
                <div className="flex gap-1 flex-shrink-0">
                  {isPending && (
                    <>
                      <Button
                        size="sm"
                        onClick={() => reviewFile(f, "approve")}
                        className="bg-emerald-600 hover:bg-emerald-700 gap-1"
                        data-testid={`approve-file-${f.id}`}
                      >
                        <CheckCircle2 className="w-3 h-3" /> Approve
                      </Button>
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => reviewFile(f, "reject")}
                        className="text-red-600 border-red-200 hover:bg-red-50 gap-1"
                        data-testid={`reject-file-${f.id}`}
                      >
                        <X className="w-3 h-3" /> Reject
                      </Button>
                    </>
                  )}
                  {canDownload && (
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => setPreviewFile(f)}
                    className="gap-1"
                    data-testid={`view-file-${f.id}`}
                    title="View / Print"
                  >
                    <Eye className="w-3 h-3" />
                  </Button>
                  )}
                  {isAdmin && (f.content_type === "application/pdf" || (f.filename || "").toLowerCase().endsWith(".pdf")) && (
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => setFillableFile(f)}
                      className={`gap-1 ${f.fillable_form?.enabled ? "text-purple-700 border-purple-300 bg-purple-50" : ""}`}
                      data-testid={`make-fillable-${f.id}`}
                      title={f.fillable_form?.enabled ? "Edit fillable fields" : "Make fillable"}
                    >
                      <FormInput className="w-3 h-3" />
                    </Button>
                  )}
                  {isAdmin && f.fillable_form?.enabled && f.source !== "form_template" && (
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => saveAsTemplate(f)}
                      className="gap-1 text-purple-700 border-purple-300"
                      data-testid={`save-template-${f.id}`}
                      title="Save as reusable template"
                    >
                      <BookmarkPlus className="w-3 h-3" />
                    </Button>
                  )}
                  {canDownload && (
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => downloadFile(f)}
                    className="gap-1"
                    data-testid={`download-file-${f.id}`}
                    title="Download"
                  >
                    <Download className="w-3 h-3" />
                  </Button>
                  )}
                  {isAdmin && (
                    <Button
                      size="sm"
                      variant="ghost"
                      onClick={() => deleteFile(f)}
                      className="text-red-600 hover:bg-red-50"
                      data-testid={`delete-file-${f.id}`}
                    >
                      <Trash2 className="w-3 h-3" />
                    </Button>
                  )}
                </div>
              </li>
              );
            })}
          </ul>
        )}
      </div>

      <IdentityApproveDialog
        employeeId={employeeId}
        file={identityApproveFile}
        onClose={() => setIdentityApproveFile(null)}
        onApproved={fetchFiles}
      />

      <FilePreviewModal
        open={!!previewFile}
        onClose={() => setPreviewFile(null)}
        file={previewFile}
        employeeId={employeeId}
      />

      <FillableFormEditor
        open={!!fillableFile}
        onClose={() => setFillableFile(null)}
        file={fillableFile}
        employeeId={employeeId}
        onSaved={fetchFiles}
      />
    </div>
  );
}
